import { ApiError } from '@platform/api-client';
import {
  contrastRatio,
  FEATURE_KEYS,
  FILE_KIND_RULES,
  isContentTypeAllowedFor,
  visibilityFor,
  type CatalogJsonSchema,
  type CatalogSchemaProperty,
  type FeatureKey,
} from '@platform/contracts';

import {
  PlatformClientError,
  type AdminIntegration,
  type AdminInstall,
  type HealthProbe,
  type InstallProbeResult,
  type IntegrationHealthCheck,
  effectiveFeature,
  settingsProblems,
  slugProblem,
  visibilityProblem,
  type AuditQuery,
  type CatalogEntry,
  type CreateCatalogEntry,
  type CreatePlanRequest,
  type CreateTenantRequest,
  type DeadLetterJob,
  type DomainAppKind,
  type ErrorGroup,
  type FeedbackQuery,
  type IntegrationCall,
  type IntegrationCallFilters,
  type IntegrationCallStatus,
  type InvitePlatformUserRequest,
  type ModerateFeedbackRequest,
  type Page,
  type PendingPromotionRow,
  type Plan,
  type PlatformAuditRow,
  type PlatformClient,
  type PlatformFeedbackItem,
  type PlatformHealth,
  type PlatformSettings,
  type PlatformSettingsPatch,
  type PlatformTenantBranding,
  type PlatformUserDetail,
  type PlatformUserListQuery,
  type PlatformUserSummary,
  type QueueSummary,
  type RequestRange,
  type RequestSummary,
  type SaveBrandingRequest,
  type TenantDetail,
  type TenantDomainDetail,
  type TenantFeature,
  type TenantHealthRow,
  type TenantInstall,
  type TenantListQuery,
  type TenantStatus,
  type TenantSummary,
  type TenantUsageRow,
  type TenantUser,
  type UpdateCatalogEntry,
  type UpdateTenantRequest,
  type UserAccountStatus,
  type UserSessionRow,
} from './platform';

/**
 * The branding fields M17.1 added to `PlatformTenantBranding` (the full clinic set),
 * all unset. Spread into the fixture tenants below.
 */
export const BRANDING_EXTRAS = {
  primaryHoverColor: null,
  primarySubtleColor: null,
  borderColor: null,
  fontFamily: null,
  emailFooterText: null,
  faviconFileId: null,
  logoUrl: null,
  faviconUrl: null,
  contrast: null,
} satisfies Partial<PlatformTenantBranding>;

/**
 * An in-memory `PlatformClient` (MASTER_PLAN M12.7, Docs/API.md §2.13, SUPER_ADMIN_UI §3.1).
 *
 * Since M12.1-M12.6 landed this is no longer a stand-in for a missing API: it is the
 * **offline** implementation the screens are tested against and what `VITE_USE_MOCKS=true`
 * renders. That makes drift the thing to guard, so it holds the contracts' own types and
 * reproduces the refusals the server actually makes:
 *
 *   1. A feature is the plan unless the tenant overrides it; `null` clears the row
 *      (ARCHITECTURE.md §17). An unknown key is a `404`, because the set is closed.
 *   2. The plan gate is checked before an install is enabled (`403 FEATURE_DISABLED`), and
 *      a `disabled` catalog entry can never be enabled whatever the plan says.
 *   3. Suspend, archive, disable, discard, reset and deactivate need a reason of at least
 *      ten characters (`ReasonSchema`).
 *   4. Slugs match `ck_tenants_slug` and a duplicate is refused.
 *   5. Promotion and demotion need `confirmation` = the target's own address; a
 *      `platform_admin` promotion opens a **pending** request a second administrator must
 *      confirm, and nobody may confirm their own.
 *   6. An operator may not revoke, reset, deactivate, promote or demote **themselves**,
 *      and the last platform admin may not be removed.
 *   7. Retention may be lengthened, never shortened (ADR-021) - a shorter value is a
 *      `409 CONFLICT`.
 *   8. A security report is never made public (rules §50.11).
 *
 * Adding a hostname, removing one and commenting on a feedback item used to have no
 * platform endpoint, so the real client borrowed them from here. All three are mounted
 * now (API.md §2.13) and nothing is borrowed: this file is the offline implementation and
 * only that. `addDomain` therefore reproduces the server's refusals too - the plan gate,
 * the platform domain, and a duplicate worded **without naming the clinic that holds it**.
 *
 * No PHI: everything here is configuration, counts and synthetic names. The two tenants
 * mirror `apps/api/prisma/seed.ts` (`heyallergy-dev` active, `demo-clinic` suspended), so
 * a screenshot taken against either implementation shows the same clinics.
 */

/** The suffix tenant subdomains hang off; the API reads it from `PLATFORM_DOMAIN`. */
export const PLATFORM_DOMAIN = 'heyallergy.app';

/** What a custom domain's CNAME points at. */
const CNAME_TARGET = `edge.${PLATFORM_DOMAIN}`;

const SEED_NOW = '2026-09-15T12:00:00.000Z';

/** The operator the auth fixtures sign in as, so "not your own account" is visible. */
const SELF_USER_ID = 'usr_ops';

export interface MockPlatformClientOptions {
  /** Injected clock (TESTING.md §2.1). */
  now?: () => Date;
  /** Start with no tenants, to look at the empty state. */
  empty?: boolean;
  /** Who the console is signed in as, for the self-action refusals. */
  currentUserId?: string;
  /**
   * Make the step-up routes (`PUT`/`DELETE …/installs/:key/credentials`) answer
   * `403 MFA_STEP_UP_REQUIRED` **once**, the way the API does when the last second factor
   * is older than ten minutes. The refusal marks the window as satisfied, standing in for
   * the verification the step-up dialog performs before it retries, so the retry
   * succeeds. Off by default: the mock's `verifyTotp` belongs to the auth client, not to
   * this seam.
   */
  requireStepUp?: boolean;
}

interface StoredTenant {
  id: string;
  slug: string;
  name: string;
  status: TenantStatus;
  planId: string;
  timeZone: string;
  statesServed: string[];
  retentionYears: number;
  createdAt: string;
  suspendedReason: string | null;
  suspendedAt: string | null;
  archivedReason: string | null;
  archivedAt: string | null;
  branding: PlatformTenantBranding;
  domains: TenantDomainDetail[];
  /** Only the keys the tenant actually overrides; everything else inherits the plan. */
  overrides: Partial<Record<FeatureKey, boolean>>;
  installs: StoredInstall[];
  users: TenantUser[];
  usage: { bookings: number; cancellations: number; notifications: number };
}

interface StoredInstall {
  integrationKey: string;
  enabledByPlatform: boolean;
  isEnabled: boolean;
  health: TenantInstall['health'];
  lastHealthAt: string | null;
  lastError: string | null;
  /** M16.2: the masked hint, settings and credential stamp. Filled lazily from the seed. */
  credHint?: Record<string, string>;
  settings?: Record<string, unknown>;
  credUpdatedAt?: string | null;
  credUpdatedBy?: string | null;
  consecutiveFailures?: number;
  /** The check history, newest first. Never contains a value, only results. */
  checks?: IntegrationHealthCheck[];
}

/* ------------------------------------------------------------------- seeds */

function planSeed(): Plan[] {
  const features = (enabled: FeatureKey[]): Record<FeatureKey, boolean> => {
    const row = {} as Record<FeatureKey, boolean>;
    for (const key of FEATURE_KEYS) row[key] = enabled.includes(key);
    return row;
  };

  return [
    {
      id: 'plan_starter',
      key: 'starter',
      name: 'Starter',
      description: 'One location, booking and email. No EHR and no AI.',
      monthlyPriceCents: 19_900,
      currency: 'USD',
      isDefault: true,
      status: 'active',
      tenantCount: 1,
      features: features(['integration.email']),
      limits: {},
    },
    {
      id: 'plan_clinic',
      key: 'clinic',
      name: 'Clinic',
      description: 'The launch plan: EHR, eligibility, payments, email and the AI estimate.',
      monthlyPriceCents: 49_900,
      currency: 'USD',
      isDefault: false,
      status: 'active',
      tenantCount: 1,
      features: features([
        'ai_estimate',
        'waitlist',
        'integration.ehr',
        'integration.eligibility',
        'integration.payment',
        'integration.email',
      ]),
      limits: { waitlist: { offersPerDay: 50 } },
    },
    {
      id: 'plan_enterprise',
      key: 'enterprise',
      name: 'Enterprise',
      description: 'Everything, including custom domains and analytics.',
      monthlyPriceCents: 129_900,
      currency: 'USD',
      isDefault: false,
      status: 'active',
      tenantCount: 0,
      features: features([...FEATURE_KEYS].filter((key) => key !== 'sms')),
      limits: {},
    },
  ];
}

/** A draft-07 object schema, the shape `CatalogJsonSchemaSchema` admits. */
function objectSchema(
  properties: Record<string, CatalogSchemaProperty>,
  required: string[] = [],
): CatalogJsonSchema {
  return { type: 'object', properties, required, additionalProperties: false };
}

const text = (description: string, secret = false): CatalogSchemaProperty =>
  secret ? { type: 'string', description, secret: true } : { type: 'string', description };

/**
 * The catalogue as it stands at launch (FR-MKT-02). The credential fields are marked
 * `secret`, which is what makes the API encrypt them and never return them - the console
 * shows the field *names* and never a value (FR-MKT-03).
 */
function catalogSeed(): CatalogEntry[] {
  const stamp = { createdAt: '2026-08-01T09:00:00.000Z', updatedAt: SEED_NOW };

  return [
    {
      id: 'int_tebra',
      key: 'tebra',
      category: 'ehr',
      name: 'Tebra',
      description:
        'Practice management and scheduling; the source of provider busy blocks and the destination for booked appointments.',
      vendor: 'Tebra (Kareo)',
      version: '1.0.0',
      capabilities: ['providers', 'busy-blocks', 'patients', 'appointments'],
      settingsSchema: objectSchema({ practiceId: text('Tebra practice id') }, ['practiceId']),
      credentialSchema: objectSchema(
        {
          customerKey: text('Customer key', true),
          user: text('API user'),
          password: text('API password', true),
        },
        ['customerKey', 'user', 'password'],
      ),
      planFeatureKey: 'integration.ehr',
      docsUrl: 'https://helpme.tebra.com/',
      status: 'available',
      ...stamp,
    },
    {
      id: 'int_stedi',
      key: 'stedi',
      category: 'eligibility',
      name: 'Stedi',
      description: 'Real-time insurance eligibility (270/271) and payer search.',
      vendor: 'Stedi',
      version: '1.0.0',
      capabilities: ['payer-search', 'eligibility-270-271'],
      settingsSchema: objectSchema({ billingNpi: text('Billing NPI used on the 270') }, [
        'billingNpi',
      ]),
      credentialSchema: objectSchema({ apiKey: text('Stedi API key', true) }, ['apiKey']),
      planFeatureKey: 'integration.eligibility',
      docsUrl: 'https://www.stedi.com/docs',
      status: 'available',
      ...stamp,
    },
    {
      id: 'int_acceptblue',
      key: 'acceptblue',
      category: 'payment',
      name: 'accept.blue',
      description: 'Card tokenisation, charges, voids and refunds, with signed webhooks.',
      vendor: 'accept.blue',
      version: '1.0.0',
      capabilities: ['tokenize', 'charge', 'void', 'refund', 'webhooks'],
      settingsSchema: objectSchema({ statementDescriptor: text('Statement descriptor') }),
      credentialSchema: objectSchema(
        { apiKey: text('API key', true), signatureKey: text('Webhook signature key', true) },
        ['apiKey', 'signatureKey'],
      ),
      planFeatureKey: 'integration.payment',
      docsUrl: 'https://docs.accept.blue/',
      status: 'available',
      ...stamp,
    },
    {
      id: 'int_mailgun',
      key: 'mailgun',
      category: 'email',
      name: 'Mailgun',
      description: 'Transactional email for confirmations and reminders, with delivery webhooks.',
      vendor: 'Sinch',
      version: '1.0.0',
      capabilities: ['send', 'delivery-webhooks'],
      settingsSchema: objectSchema(
        { domain: text('Sending domain'), fromName: text('From name') },
        ['domain'],
      ),
      credentialSchema: objectSchema(
        { apiKey: text('Mailgun API key', true), signingKey: text('Webhook signing key', true) },
        ['apiKey', 'signingKey'],
      ),
      planFeatureKey: 'integration.email',
      docsUrl: 'https://documentation.mailgun.com/',
      status: 'available',
      ...stamp,
    },
    {
      id: 'int_ai_openai',
      key: 'ai-openai',
      category: 'ai_estimate',
      name: 'OpenAI',
      description: 'Copay estimate from de-identified benefits.',
      vendor: 'OpenAI',
      version: '1.0.0',
      capabilities: ['copay-estimate'],
      settingsSchema: objectSchema({ model: text('Model name') }, ['model']),
      credentialSchema: objectSchema({ apiKey: text('OpenAI API key', true) }, ['apiKey']),
      planFeatureKey: 'ai_estimate',
      docsUrl: 'https://platform.openai.com/docs',
      status: 'available',
      ...stamp,
    },
    {
      id: 'int_ai_anthropic',
      key: 'ai-anthropic',
      category: 'ai_estimate',
      name: 'Anthropic',
      description: 'Copay estimate from de-identified benefits.',
      vendor: 'Anthropic',
      version: '1.0.0',
      capabilities: ['copay-estimate'],
      settingsSchema: objectSchema({ model: text('Model name') }, ['model']),
      credentialSchema: objectSchema({ apiKey: text('Anthropic API key', true) }, ['apiKey']),
      planFeatureKey: 'ai_estimate',
      docsUrl: 'https://docs.anthropic.com/',
      status: 'available',
      ...stamp,
    },
    {
      id: 'int_ai_google',
      key: 'ai-google',
      category: 'ai_estimate',
      name: 'Google Gemini',
      description: 'Copay estimate from de-identified benefits.',
      vendor: 'Google',
      version: '1.0.0',
      capabilities: ['copay-estimate'],
      settingsSchema: objectSchema({ model: text('Model name') }, ['model']),
      credentialSchema: objectSchema({ apiKey: text('Google AI API key', true) }, ['apiKey']),
      planFeatureKey: 'ai_estimate',
      docsUrl: 'https://ai.google.dev/docs',
      status: 'beta',
      ...stamp,
    },
    {
      id: 'int_ga4',
      key: 'analytics-ga4',
      category: 'analytics',
      name: 'Google Analytics 4',
      description: 'Server-side conversion pings with no patient identifiers.',
      vendor: 'Google',
      version: '1.0.0',
      capabilities: ['server-side-conversion'],
      settingsSchema: objectSchema({ measurementId: text('Measurement id') }, ['measurementId']),
      credentialSchema: objectSchema({ apiSecret: text('Measurement protocol secret', true) }, [
        'apiSecret',
      ]),
      planFeatureKey: 'integration.analytics',
      docsUrl: 'https://developers.google.com/analytics',
      status: 'available',
      ...stamp,
    },
    {
      id: 'int_twilio',
      key: 'twilio',
      category: 'sms',
      name: 'Twilio',
      description: 'SMS reminders. Phase 2 (FR-MKT-07): no adapter is shipped yet.',
      vendor: 'Twilio',
      version: '0.1.0',
      capabilities: ['send', 'delivery-webhooks'],
      settingsSchema: objectSchema({ messagingServiceSid: text('Messaging service SID') }, [
        'messagingServiceSid',
      ]),
      credentialSchema: objectSchema(
        { accountSid: text('Account SID'), authToken: text('Auth token', true) },
        ['accountSid', 'authToken'],
      ),
      planFeatureKey: 'sms',
      docsUrl: 'https://www.twilio.com/docs',
      status: 'disabled',
      ...stamp,
    },
  ];
}

function platformSubdomain(
  id: string,
  slug: string,
  appKind: DomainAppKind,
  checkedAt: string,
): TenantDomainDetail {
  return {
    id,
    host: appKind === 'patient' ? `${slug}.${PLATFORM_DOMAIN}` : `${slug}-admin.${PLATFORM_DOMAIN}`,
    appKind,
    kind: 'platform_subdomain',
    isPrimary: true,
    status: 'active',
    cnameTarget: null,
    txtRecord: null,
    verifiedAt: checkedAt,
    activatedAt: checkedAt,
    lastCheckAt: checkedAt,
    lastError: null,
  };
}

function tenantSeed(): StoredTenant[] {
  return [
    {
      id: 'ten_heyallergy',
      slug: 'heyallergy-dev',
      name: 'HeyAllergy',
      status: 'active',
      planId: 'plan_clinic',
      timeZone: 'America/Los_Angeles',
      statesServed: ['CA', 'NV'],
      retentionYears: 7,
      createdAt: '2026-08-04T16:10:00.000Z',
      suspendedReason: null,
      suspendedAt: null,
      archivedReason: null,
      archivedAt: null,
      branding: {
        ...BRANDING_EXTRAS,
        clinicName: 'HeyAllergy',
        logoFileId: null,
        // The extracted funnel tokens (Docs/_parity/design-tokens.md §1.1), which is what
        // `apps/api/prisma/seed.ts` writes for this tenant.
        primaryColor: '#18B3EF',
        secondaryColor: '#0E7FB0',
        accentColor: '#F57827',
        supportEmail: 'support@example.com',
        supportPhone: '+1 555 555 0100',
        privacyUrl: 'https://example.com/privacy',
        termsUrl: 'https://example.com/terms',
      },
      domains: [
        platformSubdomain('dom_hey_patient', 'heyallergy-dev', 'patient', SEED_NOW),
        platformSubdomain('dom_hey_admin', 'heyallergy-dev', 'admin', SEED_NOW),
        {
          id: 'dom_hey_custom',
          host: 'book.heyallergy.com',
          appKind: 'patient',
          kind: 'custom',
          isPrimary: false,
          status: 'pending',
          cnameTarget: CNAME_TARGET,
          txtRecord: {
            name: '_ha-verify.book.heyallergy.com',
            value: '7c1c5e9c2b1a4c6a9d1e0f9a1b2c3d4e',
          },
          verifiedAt: null,
          activatedAt: null,
          lastCheckAt: null,
          lastError: null,
        },
      ],
      overrides: { custom_domains: true },
      installs: [
        {
          integrationKey: 'tebra',
          enabledByPlatform: true,
          isEnabled: true,
          health: 'healthy',
          lastHealthAt: '2026-09-15T11:45:00.000Z',
          lastError: null,
        },
        {
          integrationKey: 'stedi',
          enabledByPlatform: true,
          isEnabled: true,
          health: 'degraded',
          lastHealthAt: '2026-09-15T11:45:00.000Z',
          lastError: 'Payer search timed out twice in the last hour.',
        },
        {
          integrationKey: 'acceptblue',
          enabledByPlatform: true,
          isEnabled: true,
          health: 'healthy',
          lastHealthAt: '2026-09-15T11:44:00.000Z',
          lastError: null,
        },
        {
          integrationKey: 'mailgun',
          enabledByPlatform: true,
          isEnabled: false,
          health: 'unconfigured',
          lastHealthAt: null,
          lastError: null,
        },
      ],
      users: [
        {
          id: 'usr_patel',
          name: 'Dr Patel',
          email: 'patel@example.com',
          role: 'clinic_admin',
          status: 'active',
          lastSeenAt: '2026-09-15T09:12:00.000Z',
        },
        {
          id: 'usr_ramos',
          name: 'Jess Ramos',
          email: 'jess@example.com',
          role: 'clinic_staff',
          status: 'active',
          lastSeenAt: '2026-09-14T17:40:00.000Z',
        },
        {
          id: 'usr_new',
          name: 'Sam Okafor',
          email: 'sam@example.com',
          role: 'clinic_staff',
          status: 'invited',
          lastSeenAt: null,
        },
      ],
      usage: { bookings: 412, cancellations: 18, notifications: 1_204 },
    },
    {
      id: 'ten_demo',
      slug: 'demo-clinic',
      name: 'Demo Clinic',
      status: 'suspended',
      planId: 'plan_starter',
      timeZone: 'America/New_York',
      statesServed: ['NY'],
      retentionYears: 7,
      createdAt: '2026-06-21T14:02:00.000Z',
      suspendedReason: 'Evaluation period ended; awaiting a signed order form.',
      suspendedAt: '2026-09-01T10:00:00.000Z',
      archivedReason: null,
      archivedAt: null,
      branding: {
        ...BRANDING_EXTRAS,
        clinicName: 'Demo Clinic',
        logoFileId: null,
        primaryColor: '#7C3AED',
        secondaryColor: null,
        accentColor: null,
        supportEmail: 'hello@example.org',
        supportPhone: null,
        privacyUrl: null,
        termsUrl: null,
      },
      domains: [platformSubdomain('dom_demo_patient', 'demo-clinic', 'patient', SEED_NOW)],
      overrides: {},
      installs: [
        {
          integrationKey: 'mailgun',
          enabledByPlatform: true,
          isEnabled: false,
          health: 'failed',
          lastHealthAt: '2026-09-12T08:00:00.000Z',
          lastError: 'Sending domain is not verified with the provider.',
        },
      ],
      users: [
        {
          id: 'usr_demo',
          name: 'Robin Vale',
          email: 'robin@example.org',
          role: 'clinic_admin',
          status: 'deactivated',
          lastSeenAt: '2026-07-02T10:00:00.000Z',
        },
      ],
      usage: { bookings: 27, cancellations: 4, notifications: 61 },
    },
  ];
}

/* ------------------------------------------------------------ M12.3 seeds */

/**
 * Forty accounts across both tenants and all three populations.
 *
 * **Patients carry an address and a name and nothing else.** Everything that makes a
 * patient record clinical lives in the clinic admin behind that tenant's permissions
 * (SECURITY.md §6.1, rules §25).
 */
function userSeed(): PlatformUserSummary[] {
  const tenants: Record<string, { name: string; slug: string }> = {
    ten_heyallergy: { name: 'HeyAllergy', slug: 'heyallergy-dev' },
    ten_demo: { name: 'Demo Clinic', slug: 'demo-clinic' },
  };

  const membership = (tenantId: string, role: string) => ({
    tenantId,
    tenantName: tenants[tenantId]?.name ?? tenantId,
    tenantSlug: tenants[tenantId]?.slug ?? tenantId,
    role,
  });

  const platform: PlatformUserSummary[] = [
    {
      id: SELF_USER_ID,
      email: 'sam.rivera@objectlabs.dev',
      name: 'Sam Rivera',
      population: 'platform',
      platformRole: 'platform_admin',
      memberships: [],
      mfaEnrolled: true,
      lastSignInAt: '2026-09-15T11:55:00.000Z',
      status: 'active',
    },
    {
      id: 'usr_ops_2',
      email: 'dana.okonkwo@objectlabs.dev',
      name: 'Dana Okonkwo',
      population: 'platform',
      platformRole: 'platform_admin',
      memberships: [],
      mfaEnrolled: true,
      lastSignInAt: '2026-09-14T08:20:00.000Z',
      status: 'active',
    },
    {
      id: 'usr_ops_3',
      email: 'lee.tran@objectlabs.dev',
      name: 'Lee Tran',
      population: 'platform',
      platformRole: 'platform_support',
      memberships: [],
      mfaEnrolled: true,
      lastSignInAt: '2026-09-15T07:02:00.000Z',
      status: 'active',
    },
    {
      id: 'usr_ops_4',
      email: 'noor.haddad@objectlabs.dev',
      name: 'Noor Haddad',
      population: 'platform',
      platformRole: null,
      memberships: [],
      mfaEnrolled: false,
      lastSignInAt: null,
      status: 'invited',
    },
  ];

  const staffSeed: [string, string, string, string, string, UserAccountStatus, boolean][] = [
    [
      'usr_patel',
      'Dr Patel',
      'patel@example.com',
      'ten_heyallergy',
      'clinic_admin',
      'active',
      true,
    ],
    [
      'usr_ramos',
      'Jess Ramos',
      'jess@example.com',
      'ten_heyallergy',
      'clinic_staff',
      'active',
      true,
    ],
    [
      'usr_new',
      'Sam Okafor',
      'sam@example.com',
      'ten_heyallergy',
      'clinic_staff',
      'invited',
      false,
    ],
    ['usr_kim', 'Ada Kim', 'ada@example.com', 'ten_heyallergy', 'clinic_staff', 'active', false],
    ['usr_bell', 'Ivy Bell', 'ivy@example.com', 'ten_heyallergy', 'clinic_staff', 'active', true],
    [
      'usr_duarte',
      'Rui Duarte',
      'rui@example.com',
      'ten_heyallergy',
      'clinic_staff',
      'deactivated',
      true,
    ],
    [
      'usr_demo',
      'Robin Vale',
      'robin@example.org',
      'ten_demo',
      'clinic_admin',
      'deactivated',
      true,
    ],
    ['usr_demo_2', 'Ana Fox', 'ana@example.org', 'ten_demo', 'clinic_staff', 'active', false],
    ['usr_demo_3', 'Tom Ibe', 'tom@example.org', 'ten_demo', 'clinic_staff', 'active', true],
    ['usr_demo_4', 'Mira Shah', 'mira@example.org', 'ten_demo', 'clinic_staff', 'invited', false],
  ];

  const staff: PlatformUserSummary[] = staffSeed.map(
    ([id, name, email, tenantId, role, status, mfaEnrolled], index) => ({
      id,
      email,
      name,
      population: 'staff',
      platformRole: null,
      memberships: [membership(tenantId, role)],
      mfaEnrolled,
      lastSignInAt:
        status === 'invited'
          ? null
          : `2026-09-${String(5 + (index % 10)).padStart(2, '0')}T14:05:00.000Z`,
      status,
    }),
  );

  const patientNames = [
    'Alex Moreno',
    'Bea Lindqvist',
    'Cyrus Adeyemi',
    'Dita Novak',
    'Eli Sorensen',
    'Farah Nasser',
    'Gil Ferreira',
    'Hana Ito',
    'Iris Kowalski',
    'Jonah Reyes',
    'Kira Petrov',
    'Liam Doyle',
    'Mona Farid',
    'Nils Berger',
    'Omar Sissoko',
    'Priya Raman',
    'Quinn Alvarez',
    'Rosa Bianchi',
    'Sven Larsen',
    'Tara Mensah',
    'Uma Chandra',
    'Viktor Horvat',
    'Wren Ashby',
    'Xenia Popa',
    'Yusuf Demir',
    'Zoe Karras',
  ];

  const patients: PlatformUserSummary[] = patientNames.map((name, index) => {
    const tenantId = index % 3 === 0 ? 'ten_demo' : 'ten_heyallergy';
    const handle = name.toLowerCase().replace(/[^a-z]+/g, '.');
    return {
      id: `usr_pat_${String(index + 1).padStart(2, '0')}`,
      email: `${handle}@example.net`,
      name,
      population: 'patient' as const,
      platformRole: null,
      memberships: [membership(tenantId, 'patient')],
      /* Patients are not required to enrol a second factor (SECURITY.md §4.3). */
      mfaEnrolled: index % 7 === 0,
      lastSignInAt:
        index % 5 === 4
          ? null
          : `2026-09-${String(1 + (index % 14)).padStart(2, '0')}T18:30:00.000Z`,
      status: index % 11 === 3 ? ('deactivated' as const) : ('active' as const),
    };
  });

  return [...platform, ...staff, ...patients];
}

/**
 * Sessions for the drawer.
 *
 * `ipHash` is the first twelve characters of the stored hash and never an address: an
 * operator needs to tell two clients apart, not to locate somebody (SECURITY.md §7).
 */
function sessionSeed(userId: string): UserSessionRow[] {
  if (userId === SELF_USER_ID) {
    return [
      {
        id: 'ses_current',
        userAgent: 'Chrome 140 on macOS',
        ipHash: 'a41f9c2e7b03',
        createdAt: '2026-09-15T09:58:00.000Z',
        lastSeenAt: '2026-09-15T11:58:00.000Z',
        expiresAt: '2026-09-15T17:58:00.000Z',
        isCurrent: true,
      },
    ];
  }
  return [
    {
      id: `ses_${userId}_a`,
      userAgent: 'Chrome 140 on Windows',
      ipHash: '55d0e1a9f772',
      createdAt: '2026-09-15T08:10:00.000Z',
      lastSeenAt: '2026-09-15T11:31:00.000Z',
      expiresAt: '2026-09-15T20:10:00.000Z',
      isCurrent: false,
    },
    {
      id: `ses_${userId}_b`,
      userAgent: 'Safari 18 on iPhone',
      ipHash: 'c9b4207e1d63',
      createdAt: '2026-09-12T19:44:00.000Z',
      lastSeenAt: '2026-09-14T07:12:00.000Z',
      expiresAt: '2026-09-16T19:44:00.000Z',
      isCurrent: false,
    },
  ];
}

/* ------------------------------------------------------------ M12.4 seeds */

/** Eighty `audit_log` rows across both tenants and the platform. */
function auditSeed(): PlatformAuditRow[] {
  const shapes: {
    action: string;
    tableName: string;
    before: Record<string, unknown> | null;
    after: Record<string, unknown> | null;
  }[] = [
    {
      action: 'update',
      tableName: 'appointment_types',
      before: { name: 'Allergy consult', durationMinutes: 30, isActive: true },
      after: { name: 'Allergy consult', durationMinutes: 45, isActive: true },
    },
    {
      action: 'update',
      tableName: 'integration_installs',
      /* The API redacts these; the diff redacts them again (SECURITY.md §7.4). */
      before: { isEnabled: false, apiKey: 'sk_live_9f2c…', signatureKey: 'whsec_…' },
      after: { isEnabled: true, apiKey: 'sk_live_4b71…', signatureKey: 'whsec_…' },
    },
    {
      action: 'event.superadmin.promoted',
      tableName: 'event',
      before: { role: null },
      after: { role: 'platform_support', reason: 'Joining the on-call rota this quarter.' },
    },
    {
      action: 'event.impersonation.started',
      tableName: 'event',
      before: null,
      after: { targetUserId: 'usr_patel', reason: 'Debugging a failed booking, ticket 412.' },
    },
    {
      action: 'insert',
      tableName: 'pricing_rules',
      before: null,
      after: { appointmentType: 'skin-test', selfPayCents: 24_000 },
    },
    {
      action: 'delete',
      tableName: 'provider_schedules',
      before: { providerId: 'prv_3', weekday: 'friday', start: '09:00', end: '13:00' },
      after: null,
    },
    {
      action: 'update',
      tableName: 'tenant_features',
      before: { featureKey: 'custom_domains', enabled: false },
      after: { featureKey: 'custom_domains', enabled: true },
    },
    {
      action: 'event.platform.cross_tenant_access',
      tableName: 'event',
      before: null,
      after: { route: 'GET /platform/integrations/calls', targetTenantId: 'ten_demo' },
    },
  ];

  /*
   * `actorId` and `actorRole` are nullable on the wire: a row written by a job or by the
   * retention sweeper has no person behind it, and the table says "System" rather than
   * inventing one.
   */
  const actors: [string | null, string, string | null][] = [
    [SELF_USER_ID, 'sam.rivera@objectlabs.dev', 'platform_admin'],
    ['usr_ops_3', 'lee.tran@objectlabs.dev', 'platform_support'],
    ['usr_patel', 'patel@example.com', 'clinic_admin'],
    ['usr_ramos', 'jess@example.com', 'clinic_staff'],
    [null, 'System', null],
  ];

  const tenants: [string | null, string | null][] = [
    ['ten_heyallergy', 'HeyAllergy'],
    ['ten_demo', 'Demo Clinic'],
    [null, null],
  ];

  return Array.from({ length: 80 }, (_unused, index) => {
    const shape = shapes[index % shapes.length] as (typeof shapes)[number];
    const actor = actors[index % actors.length] as (typeof actors)[number];
    const tenant = tenants[
      shape.tableName === 'event' && index % 3 === 2 ? 2 : index % 2
    ] as (typeof tenants)[number];

    return {
      id: `aud_${String(index + 1).padStart(3, '0')}`,
      tenantId: tenant[0],
      tenantName: tenant[1],
      actorId: actor[0],
      actorLabel: actor[1],
      actorRole: actor[2],
      impersonatorLabel: index % 19 === 5 ? 'sam.rivera@objectlabs.dev' : null,
      action: shape.action,
      tableName: shape.tableName,
      rowId: shape.tableName === 'event' ? null : `row_${String(index + 1).padStart(3, '0')}`,
      before: shape.before,
      after: shape.after,
      correlationId: `web-${String(index + 1).padStart(4, '0')}`,
      occurredAt: new Date(Date.parse(SEED_NOW) - index * 17 * 60_000).toISOString(),
    } satisfies PlatformAuditRow;
  });
}

/** Sixty `integration_calls` rows, newest first, the way the endpoint returns them. */
function integrationCallSeed(): IntegrationCall[] {
  const shapes: [string, string, IntegrationCallStatus, number | null, string | null][] = [
    ['tebra', 'appointments.create', 'ok', 412, null],
    ['tebra', 'providers.busy_blocks', 'ok', 268, null],
    ['stedi', 'eligibility.check', 'ok', 1_180, null],
    ['stedi', 'eligibility.check', 'timeout', null, 'GatewayTimeoutError'],
    ['acceptblue', 'charges.create', 'ok', 640, null],
    ['acceptblue', 'charges.refund', 'error', 502, 'VendorUnavailableError'],
    ['mailgun', 'messages.send', 'ok', 220, null],
    ['mailgun', 'messages.send', 'error', 401, 'CredentialRejectedError'],
    ['tebra', 'patients.upsert', 'rate_limited', 980, 'TooManyRequestsError'],
    ['stedi', 'payers.search', 'ok', 310, null],
  ];

  return Array.from({ length: 60 }, (_unused, index) => {
    const shape = shapes[index % shapes.length] as (typeof shapes)[number];
    const httpStatus =
      shape[2] === 'ok' ? 200 : typeof shape[3] === 'number' && shape[3] > 400 ? shape[3] : null;

    return {
      id: `00000000-0000-4000-8000-${String(index + 1).padStart(12, '0')}`,
      tenantId: index % 4 === 3 ? 'ten_demo' : 'ten_heyallergy',
      integrationKey: shape[0],
      operation: shape[1],
      status: shape[2],
      httpStatus,
      latencyMs: shape[3],
      errorClass: shape[4],
      correlationId: `job-${String(index + 1).padStart(4, '0')}`,
      jobId: index % 3 === 0 ? `bos_${String(index + 1).padStart(4, '0')}` : null,
      summary: { items: (index % 5) + 1, bytes: 512 + index * 8 },
      startedAt: new Date(Date.parse(SEED_NOW) - index * 7 * 60_000).toISOString(),
    } as IntegrationCall;
  });
}

/**
 * The eight pg-boss queues (ADR-006).
 *
 * `depth` is `created + retry`, the way the endpoint composes it, so the number on the
 * screen adds up to the two beside it.
 */
function queueSeed(): QueueSummary[] {
  const queue = (
    name: string,
    created: number,
    retry: number,
    active: number,
    failed: number,
    oldestPendingAt: string | null,
  ): QueueSummary => ({
    name,
    depth: created + retry,
    created,
    retry,
    active,
    failed,
    deadLettered: 0,
    oldestPendingAt,
  });

  return [
    queue('ehr', 10, 2, 2, 3, '2026-09-15T11:41:00.000Z'),
    queue('sync', 4, 0, 1, 0, '2026-09-15T11:57:00.000Z'),
    queue('eligibility', 6, 1, 1, 1, '2026-09-15T11:52:00.000Z'),
    queue('payments', 0, 0, 0, 0, null),
    queue('notifications', 21, 2, 3, 2, '2026-09-15T11:30:00.000Z'),
    queue('reminders', 148, 0, 0, 0, '2026-09-15T12:00:00.000Z'),
    queue('ai', 1, 0, 0, 0, '2026-09-15T11:59:00.000Z'),
    queue('maintenance', 0, 0, 0, 0, null),
  ];
}

function deadLetterSeed(): DeadLetterJob[] {
  return [
    {
      id: 'bos_dl_ehr_01',
      queue: 'ehr__dlq',
      sourceQueue: 'ehr',
      jobName: 'ehr.push_appointment',
      tenantId: 'ten_heyallergy',
      errorClass: 'VendorUnavailableError',
      errorMessage: 'Tebra answered 503 on five consecutive attempts.',
      attempts: 5,
      firstFailedAt: '2026-09-15T06:12:00.000Z',
      lastFailedAt: '2026-09-15T10:48:00.000Z',
    },
    {
      id: 'bos_dl_notif_01',
      queue: 'notifications__dlq',
      sourceQueue: 'notifications',
      jobName: 'notifications.send',
      tenantId: 'ten_demo',
      errorClass: 'CredentialRejectedError',
      errorMessage: 'Mailgun rejected the API key for this sending domain.',
      attempts: 5,
      firstFailedAt: '2026-09-14T22:03:00.000Z',
      lastFailedAt: '2026-09-15T09:17:00.000Z',
    },
  ];
}

/** Route × status counts with latency percentiles (`request_logs`). */
function requestSeed(range: RequestRange): RequestSummary {
  const multiplier = range === '24h' ? 1 : range === '7d' ? 6 : 24;
  const rows: [string, string, string, number, number, number, number, number][] = [
    ['GET', '/api/v1/public/availability', 'public', 4_820, 61, 0, 38, 121],
    ['POST', '/api/v1/public/bookings/reserve', 'public', 1_190, 44, 2, 72, 210],
    ['POST', '/api/v1/public/bookings/confirm', 'public', 806, 12, 1, 143, 498],
    ['GET', '/api/v1/admin/appointments', 'admin', 2_140, 8, 0, 54, 168],
    ['GET', '/api/v1/auth/session', 'auth', 9_530, 210, 0, 11, 34],
    ['POST', '/api/v1/webhooks/:vendor/:tenantKey', 'webhook', 612, 9, 3, 28, 96],
    ['GET', '/api/v1/platform/tenants', 'platform', 148, 0, 0, 41, 118],
  ];

  const scaled = rows.map(
    ([method, route, surface, ok, clientError, serverError, p50Ms, p95Ms]) => ({
      method,
      route,
      surface,
      ok: ok * multiplier,
      clientError: clientError * multiplier,
      serverError: serverError * multiplier,
      total: (ok + clientError + serverError) * multiplier,
      p50Ms,
      p95Ms,
    }),
  );

  const total = scaled.reduce((sum, row) => sum + row.total, 0);
  const serverErrors = scaled.reduce((sum, row) => sum + row.serverError, 0);
  const days = range === '24h' ? 1 : range === '7d' ? 7 : 30;

  return {
    range,
    from: new Date(Date.parse(SEED_NOW) - days * 24 * 3_600_000).toISOString(),
    to: SEED_NOW,
    total,
    errorRate: total === 0 ? 0 : serverErrors / total,
    p50Ms: 34,
    p95Ms: 210,
    rows: scaled,
  };
}

/**
 * Error groups as `GET /platform/logs/errors` composes them: `(surface, route, status)`
 * plus the error code from the matching audit rows. `url` is null until a Sentry
 * organisation is set in platform settings (ADR-020).
 */
function errorSeed(sentryOrgUrl: string | null): ErrorGroup[] {
  const link = (slug: string): string | null =>
    sentryOrgUrl === null ? null : `${sentryOrgUrl.replace(/\/$/, '')}/issues/?query=${slug}`;

  return [
    {
      id: 'err_1',
      code: 'INTEGRATION_UNAVAILABLE',
      message: 'Tebra answered 503 for appointments.create',
      surface: 'worker',
      route: 'job:ehr.push_appointment',
      status: 503,
      count: 34,
      tenantCount: 1,
      lastSeenAt: '2026-09-15T10:48:00.000Z',
      url: link('INTEGRATION_UNAVAILABLE'),
    },
    {
      id: 'err_2',
      code: 'SLOT_TAKEN',
      message: 'Unique constraint failed on ux_slot_reservation',
      surface: 'public',
      route: 'POST /api/v1/public/bookings/reserve',
      status: 409,
      count: 11,
      tenantCount: 2,
      lastSeenAt: '2026-09-15T09:02:00.000Z',
      url: link('SLOT_TAKEN'),
    },
    {
      id: 'err_3',
      code: 'INTERNAL',
      message: 'An unexpected error occurred.',
      surface: 'admin',
      route: 'GET /api/v1/admin/appointments',
      status: 500,
      count: 4,
      tenantCount: 1,
      lastSeenAt: '2026-09-14T16:20:00.000Z',
      url: link('INTERNAL'),
    },
  ];
}

/* ------------------------------------------------------------ M12.6 seeds */

function settingsSeed(): PlatformSettings {
  return {
    platformDomain: PLATFORM_DOMAIN,
    cnameTarget: CNAME_TARGET,
    defaultTemplates: [
      { key: 'booking.confirmation', name: 'Booking confirmation', channel: 'email' },
      { key: 'booking.reminder_24h', name: 'Reminder, 24 hours before', channel: 'email' },
      { key: 'booking.reminder_30m', name: 'Reminder, 30 minutes before', channel: 'email' },
      { key: 'booking.rescheduled', name: 'Appointment rescheduled', channel: 'email' },
      { key: 'booking.cancelled', name: 'Appointment cancelled', channel: 'email' },
      { key: 'payment.receipt', name: 'Payment receipt', channel: 'email' },
      { key: 'intake.reminder', name: 'Intake form reminder', channel: 'email' },
      { key: 'waitlist.offer', name: 'Waitlist offer', channel: 'email' },
    ],
    pricingTexts: {
      selfPay: 'Your visit is {{amount}}, payable at {{clinicName}} on the day.',
      copayEstimate:
        'Based on your plan with {{payerName}}, we estimate {{amount}}. This is an estimate, not a bill.',
      pendingReview:
        '{{clinicName}} is checking your benefits and will confirm your price before your visit.',
    },
    retentionYears: 7,
    defaultPlanKey: 'starter',
    defaultTimeZone: 'America/Los_Angeles',
    defaultBranding: { primaryColor: '#18B3EF', secondaryColor: null, accentColor: null },
    maintenanceMode: { enabled: false, message: '' },
    sentryUrl: 'https://objectlabs.sentry.io/organizations/objectlabs',
    monitorUrl: 'https://portal.azure.com/#view/AppInsights/heyallergy',
    contacts: [
      { name: 'Object Labs on-call', role: 'Platform on-call', email: 'oncall@objectlabs.dev' },
      { name: 'Sam Rivera', role: 'Platform admin', email: 'sam.rivera@objectlabs.dev' },
      { name: 'Security reports', role: 'Private intake', email: 'security@objectlabs.dev' },
    ],
  };
}

/* ------------------------------------------------------------ M12.8 seeds */

/** Eight feedback items across both tenants and the platform itself (rules §50). */
function feedbackSeed(): PlatformFeedbackItem[] {
  const base = {
    votedByMe: false,
    mine: false,
    response: null,
    respondedAt: null,
    attachmentFileIds: [] as string[],
  };

  return [
    {
      ...base,
      id: 'fb_1',
      tenantId: 'ten_heyallergy',
      tenantName: 'HeyAllergy',
      app: 'admin',
      category: 'bug',
      title: 'Calendar jumps a week when I change provider',
      description: 'Switching provider on the week view moves the date back seven days.',
      status: 'planned',
      visibility: 'public',
      submitterName: 'Dr Patel',
      createdAt: '2026-09-12T15:04:00.000Z',
      updatedAt: '2026-09-13T08:30:00.000Z',
      votes: 6,
      duplicateOfId: null,
      comments: [
        {
          id: 'fbc_1',
          authorName: 'Sam Rivera',
          body: 'Reproduced on the week grid. Scheduled for the next scheduling pass.',
          isInternal: false,
          createdAt: '2026-09-13T08:30:00.000Z',
        },
      ],
    },
    {
      ...base,
      id: 'fb_2',
      tenantId: 'ten_heyallergy',
      tenantName: 'HeyAllergy',
      app: 'patient',
      category: 'feature',
      title: 'Let patients add a second insurance card',
      description: 'Several patients have secondary coverage and can only enter one card.',
      status: 'planned',
      visibility: 'public',
      submitterName: 'Jess Ramos',
      createdAt: '2026-09-10T11:20:00.000Z',
      updatedAt: '2026-09-10T11:20:00.000Z',
      votes: 14,
      duplicateOfId: null,
      comments: [],
    },
    {
      ...base,
      id: 'fb_3',
      tenantId: 'ten_heyallergy',
      tenantName: 'HeyAllergy',
      app: 'admin',
      category: 'bug',
      title: 'Week view loses the date when the provider changes',
      description: 'Same as the calendar problem reported last week.',
      status: 'open',
      visibility: 'public',
      submitterName: 'Ivy Bell',
      createdAt: '2026-09-14T09:41:00.000Z',
      updatedAt: '2026-09-14T09:41:00.000Z',
      votes: 1,
      duplicateOfId: null,
      comments: [],
    },
    {
      ...base,
      id: 'fb_4',
      tenantId: 'ten_demo',
      tenantName: 'Demo Clinic',
      app: 'admin',
      category: 'security',
      title: 'Staff invite link stayed valid after the invite was cancelled',
      description: 'Reported privately. Details are in the internal comment.',
      status: 'in_progress',
      visibility: 'private',
      submitterName: 'Tom Ibe',
      createdAt: '2026-09-11T19:05:00.000Z',
      updatedAt: '2026-09-12T07:55:00.000Z',
      votes: 0,
      duplicateOfId: null,
      comments: [
        {
          id: 'fbc_2',
          authorName: 'Dana Okonkwo',
          body: 'Confirmed; invitation rows are not revoked on cancel. Tracked internally.',
          isInternal: true,
          createdAt: '2026-09-12T07:55:00.000Z',
        },
      ],
    },
    {
      ...base,
      id: 'fb_5',
      tenantId: 'ten_demo',
      tenantName: 'Demo Clinic',
      app: 'patient',
      category: 'general',
      title: 'The booking confirmation email reads well',
      description: 'Just saying thank you.',
      status: 'shipped',
      visibility: 'public',
      submitterName: 'Ana Fox',
      createdAt: '2026-09-08T13:12:00.000Z',
      updatedAt: '2026-09-08T13:12:00.000Z',
      votes: 3,
      duplicateOfId: null,
      comments: [],
    },
    {
      ...base,
      id: 'fb_6',
      tenantId: 'ten_demo',
      tenantName: 'Demo Clinic',
      app: 'patient',
      category: 'bug',
      title: 'Phone number field rejects a valid +1 number',
      description: 'Entering the country code fails validation.',
      status: 'open',
      visibility: 'private',
      submitterName: 'Mira Shah',
      createdAt: '2026-09-15T08:46:00.000Z',
      updatedAt: '2026-09-15T08:46:00.000Z',
      votes: 0,
      duplicateOfId: null,
      comments: [],
    },
    {
      ...base,
      id: 'fb_7',
      tenantId: null,
      tenantName: null,
      app: 'super',
      category: 'feature',
      title: 'Show the dead-letter count on the overview',
      description: 'Support would rather not open Logs to find out.',
      status: 'in_progress',
      visibility: 'private',
      submitterName: 'Lee Tran',
      createdAt: '2026-09-13T16:30:00.000Z',
      updatedAt: '2026-09-13T16:30:00.000Z',
      votes: 2,
      duplicateOfId: null,
      comments: [],
    },
    {
      ...base,
      id: 'fb_8',
      tenantId: null,
      tenantName: null,
      app: 'super',
      category: 'general',
      title: 'Audit filters should remember the last tenant',
      description: 'Every visit starts from all tenants again.',
      status: 'declined',
      visibility: 'private',
      submitterName: 'Sam Rivera',
      createdAt: '2026-09-09T10:02:00.000Z',
      updatedAt: '2026-09-09T10:02:00.000Z',
      votes: 0,
      duplicateOfId: null,
      comments: [],
    },
  ];
}

/* ------------------------------------------------------------------ client */

/** A stand-in for the signed URL the API answers for a stored mark. Never fetched. */
function fakeDownloadUrl(fileId: string): string {
  return `https://storage.example.test/download/${fileId}`;
}

/** `BrandingContrast`, computed the way the API does (`contrastRatio` from the contracts). */
function contrastOf(primary: string | null): PlatformTenantBranding['contrast'] {
  if (primary === null) return null;
  const ratio = Math.round(contrastRatio(primary, '#ffffff') * 100) / 100;
  return { onPrimary: ratio, primaryOnSurface: ratio, passesAA: ratio >= 4.5 };
}

export function createMockPlatformClient(options: MockPlatformClientOptions = {}): PlatformClient {
  const now = options.now ?? ((): Date => new Date(SEED_NOW));
  const self = options.currentUserId ?? SELF_USER_ID;
  const plans = planSeed();
  const catalog = catalogSeed();
  const tenants = options.empty === true ? [] : tenantSeed();
  let sequence = 0;

  const nextId = (prefix: string): string => {
    sequence += 1;
    return `${prefix}_${String(sequence).padStart(4, '0')}`;
  };

  /** Files uploaded through `uploadTenantFile`: whose, and which kind (M17.2). */
  const uploads = new Map<string, { tenantId: string; kind: 'logo' | 'favicon' }>();
  let uploadSequence = 0;

  const users = options.empty === true ? [] : userSeed();
  const sessions: Record<string, UserSessionRow[]> = {};
  /*
   * Rows rather than bare `PendingPromotion`s, because `GET /platform/promotions` carries
   * who asked and who it is for: the second administrator has to recognise the person
   * they are about to give the keys to without a round trip per row.
   */
  const promotions: PendingPromotionRow[] = [];
  const audit = options.empty === true ? [] : auditSeed();
  const calls = options.empty === true ? [] : integrationCallSeed();
  const queues = queueSeed();
  const deadLetters = options.empty === true ? [] : deadLetterSeed();
  const settings = settingsSeed();
  const feedback = options.empty === true ? [] : feedbackSeed();

  /** `ReasonSchema`: ten characters, because "test" and "asdf" both fit in nine. */
  const requireReason = (reason: string): void => {
    if (reason.trim().length < 10) {
      throw new PlatformClientError('Say why, in at least 10 characters. It is audited.');
    }
  };

  /** Rules §35.2: an operator may not act on their own account. */
  const refuseSelf = (userId: string, what: string): void => {
    if (userId === self) {
      throw new PlatformClientError(`You cannot ${what} on your own account.`);
    }
  };

  const reject = <T>(message: string): Promise<T> =>
    Promise.reject(new PlatformClientError(message));

  const paged = <Row>(rows: Row[], cursor: string | null | undefined, limit: number): Page<Row> => {
    const start = cursor == null ? 0 : Number.parseInt(cursor, 10);
    const slice = rows.slice(start, start + limit);
    return {
      data: slice,
      page: { nextCursor: start + limit < rows.length ? String(start + limit) : null, limit },
    };
  };

  const cloneUser = (user: PlatformUserSummary): PlatformUserSummary => ({
    ...user,
    memberships: user.memberships.map((row) => ({ ...row })),
  });

  const findUser = (userId: string): PlatformUserSummary => {
    const user = users.find((candidate) => candidate.id === userId);
    if (user === undefined) throw new PlatformClientError('That account does not exist.');
    return user;
  };

  const userDetail = (userId: string): PlatformUserDetail => {
    const user = findUser(userId);
    return {
      user: cloneUser(user),
      sessions: (sessions[userId] ?? sessionSeed(userId)).map((row) => ({ ...row })),
      mfa: {
        enrolled: user.mfaEnrolled,
        methods: user.mfaEnrolled
          ? user.population === 'platform'
            ? ['totp', 'backup_codes', 'passkey']
            : ['totp', 'backup_codes']
          : [],
        lastVerifiedAt: user.mfaEnrolled ? user.lastSignInAt : null,
      },
    };
  };

  const admins = (): PlatformUserSummary[] =>
    users.filter((user) => user.platformRole === 'platform_admin' && user.status === 'active');

  const cloneSettings = (value: PlatformSettings): PlatformSettings => ({
    ...value,
    defaultTemplates: value.defaultTemplates.map((row) => ({ ...row })),
    pricingTexts: { ...value.pricingTexts },
    defaultBranding: { ...value.defaultBranding },
    maintenanceMode: { ...value.maintenanceMode },
    contacts: value.contacts.map((row) => ({ ...row })),
  });

  const cloneFeedback = (item: PlatformFeedbackItem): PlatformFeedbackItem => ({
    ...item,
    comments: item.comments.map((comment) => ({ ...comment })),
    attachmentFileIds: [...item.attachmentFileIds],
  });

  const planOf = (tenant: StoredTenant): Plan => {
    const plan = plans.find((candidate) => candidate.id === tenant.planId);
    if (plan === undefined) throw new PlatformClientError('That plan no longer exists.');
    return plan;
  };

  const find = (tenantId: string): StoredTenant => {
    const tenant = tenants.find((candidate) => candidate.id === tenantId);
    if (tenant === undefined) throw new PlatformClientError('We could not find that clinic.');
    return tenant;
  };

  const featuresOf = (tenant: StoredTenant): TenantFeature[] => {
    const plan = planOf(tenant);
    return FEATURE_KEYS.map((key) => {
      const planDefault = plan.features[key];
      const override = tenant.overrides[key] ?? null;
      return { key, planDefault, override, effective: effectiveFeature(planDefault, override) };
    });
  };

  const catalogEntry = (key: string): CatalogEntry => {
    const entry = catalog.find((candidate) => candidate.key === key);
    if (entry === undefined) {
      throw new PlatformClientError('That integration is not in the catalog.');
    }
    return entry;
  };

  const installsOf = (tenant: StoredTenant): TenantInstall[] => {
    const features = featuresOf(tenant);
    const allows = (key: FeatureKey | null): boolean =>
      key === null || features.find((feature) => feature.key === key)?.effective === true;

    return catalog.map((entry) => {
      const stored = tenant.installs.find((install) => install.integrationKey === entry.key);
      return {
        integrationKey: entry.key,
        name: entry.name,
        vendor: entry.vendor,
        category: entry.category,
        planFeatureKey: entry.planFeatureKey,
        planAllows: allows(entry.planFeatureKey),
        enabledByPlatform: stored?.enabledByPlatform ?? false,
        isEnabled: stored?.isEnabled ?? false,
        health: stored?.health ?? 'unconfigured',
        lastHealthAt: stored?.lastHealthAt ?? null,
        lastError: stored?.lastError ?? null,
        catalogStatus: entry.status,
      };
    });
  };

  const healthOf = (tenant: StoredTenant) => {
    const health = { healthy: 0, degraded: 0, failed: 0, unconfigured: 0 };
    for (const install of tenant.installs) {
      if (!install.enabledByPlatform) continue;
      /* `configured` is a state the summary folds into "healthy": the install works and
         nobody needs to be told about the difference on a list row. */
      if (install.health === 'configured') health.healthy += 1;
      else health[install.health] += 1;
    }
    return health;
  };

  const summarise = (tenant: StoredTenant): TenantSummary => {
    const plan = planOf(tenant);
    return {
      id: tenant.id,
      slug: tenant.slug,
      name: tenant.name,
      status: tenant.status,
      planId: plan.id,
      planKey: plan.key,
      planName: plan.name,
      timeZone: tenant.timeZone,
      statesServed: [...tenant.statesServed],
      domainCount: tenant.domains.length,
      createdAt: tenant.createdAt,
      health: healthOf(tenant),
      suspendedReason: tenant.suspendedReason,
      suspendedAt: tenant.suspendedAt,
      archivedReason: tenant.archivedReason,
      archivedAt: tenant.archivedAt,
    };
  };

  const usageOf = (tenant: StoredTenant) => ({
    from: new Date(Date.parse(SEED_NOW) - 30 * 24 * 3_600_000).toISOString(),
    to: SEED_NOW,
    bookings: tenant.usage.bookings,
    cancellations: tenant.usage.cancellations,
    notifications: tenant.usage.notifications,
    integrationCalls: calls.filter((call) => call.tenantId === tenant.id).length,
    integrationErrors: calls.filter((call) => call.tenantId === tenant.id && call.status !== 'ok')
      .length,
    activeProviders: tenant.status === 'active' ? 3 : 0,
    staffUsers: tenant.users.length,
  });

  const detail = (tenant: StoredTenant): TenantDetail => ({
    tenant: summarise(tenant),
    domains: tenant.domains.map((domain) => ({
      id: domain.id,
      host: domain.host,
      appKind: domain.appKind,
      kind: domain.kind,
      isPrimary: domain.isPrimary,
      status: domain.status,
      verificationToken: domain.txtRecord?.value ?? null,
      lastCheckAt: domain.lastCheckAt,
      lastError: domain.lastError,
    })),
    branding: { ...tenant.branding },
    features: featuresOf(tenant),
    installs: healthOf(tenant),
    users: tenant.users.map((user) => ({ ...user })),
    usage: usageOf(tenant),
  });

  const storedInstall = (tenant: StoredTenant, key: string): StoredInstall => {
    const existing = tenant.installs.find((install) => install.integrationKey === key);
    if (existing !== undefined) return existing;
    const created: StoredInstall = {
      integrationKey: key,
      enabledByPlatform: false,
      isEnabled: false,
      health: 'unconfigured',
      lastHealthAt: null,
      lastError: null,
    };
    tenant.installs.push(created);
    return created;
  };

  const install = (tenant: StoredTenant, key: string): TenantInstall => {
    const row = installsOf(tenant).find((candidate) => candidate.integrationKey === key);
    if (row === undefined) throw new PlatformClientError('That integration does not exist.');
    return row;
  };

  /* ------------------------------------------------ install detail (M16.2) */

  /** The one step-up window the mock models; see `requireStepUp`. */
  let stepUpSatisfied = options.requireStepUp !== true;
  const requireStepUp = (): void => {
    if (stepUpSatisfied) return;
    stepUpSatisfied = true;
    throw new ApiError({
      status: 403,
      code: 'MFA_STEP_UP_REQUIRED',
      message: 'Confirm it is you with a second factor to continue.',
      correlationId: 'mock',
    });
  };

  /** A seeded install that is past `unconfigured` has a bundle; give it a plausible hint. */
  const hydrate = (stored: StoredInstall, entry: CatalogEntry): Required<StoredInstall> => {
    if (stored.credHint === undefined) {
      stored.credHint = {};
      if (stored.health !== 'unconfigured') {
        for (const [name, property] of Object.entries(entry.credentialSchema.properties)) {
          if (property.secret === true) stored.credHint[name] = '••••7c21';
        }
      }
    }
    stored.settings ??= {};
    stored.credUpdatedAt ??= stored.health === 'unconfigured' ? null : '2026-09-01T10:00:00.000Z';
    stored.credUpdatedBy ??= stored.health === 'unconfigured' ? null : 'Clinic admin';
    stored.consecutiveFailures ??= stored.health === 'failed' ? 3 : 0;
    stored.checks ??=
      stored.lastHealthAt === null
        ? []
        : [
            {
              id: `00000000-0000-4000-9000-${String(stored.integrationKey.length).padStart(12, '0')}`,
              result:
                stored.health === 'failed'
                  ? 'failed'
                  : stored.health === 'degraded'
                    ? 'degraded'
                    : 'healthy',
              latencyMs: 240,
              errorClass: null,
              message: stored.lastError,
              checkedAt: stored.lastHealthAt,
            },
          ];
    return stored as Required<StoredInstall>;
  };

  const adminInstall = (tenant: StoredTenant, entry: CatalogEntry): AdminInstall | null => {
    const stored = tenant.installs.find((row) => row.integrationKey === entry.key);
    if (stored === undefined || !stored.enabledByPlatform) return null;
    const row = hydrate(stored, entry);
    return {
      id: `00000000-0000-4000-a000-${String(tenant.installs.indexOf(stored) + 1).padStart(12, '0')}`,
      enabledByPlatform: row.enabledByPlatform,
      isEnabled: row.isEnabled,
      health: row.health,
      lastHealthAt: row.lastHealthAt,
      lastError: row.lastError,
      consecutiveFailures: row.consecutiveFailures,
      settings: { ...row.settings },
      credHint: { ...row.credHint },
      credUpdatedAt: row.credUpdatedAt,
      credUpdatedBy: row.credUpdatedBy,
      credentialsSet: Object.keys(row.credHint).length > 0,
      webhookUrl: entry.capabilities.includes('webhooks')
        ? `https://api.${PLATFORM_DOMAIN}/api/v1/webhooks/${entry.key}/whk_${tenant.slug}`
        : null,
    };
  };

  const adminIntegration = (tenant: StoredTenant, entry: CatalogEntry): AdminIntegration => {
    const { id: _id, createdAt: _created, updatedAt: _updated, ...integration } = entry;
    return { integration, install: adminInstall(tenant, entry) };
  };

  /** The enabled install, or the refusal the API gives for one that is not. */
  const enabledInstall = (
    tenant: StoredTenant,
    key: string,
  ): { entry: CatalogEntry; stored: Required<StoredInstall> } => {
    const entry = catalogEntry(key);
    const stored = tenant.installs.find((row) => row.integrationKey === key);
    if (stored === undefined || !stored.enabledByPlatform) {
      throw new ApiError({
        status: 403,
        code: 'FEATURE_DISABLED',
        message: 'This integration is not enabled for this clinic. Enable it first.',
        correlationId: 'mock',
      });
    }
    return { entry, stored: hydrate(stored, entry) };
  };

  /**
   * The mock's `testConnection()`: a value that starts with `invalid` is refused by the
   * "vendor"; anything else answers healthy. The message is what the API would serve,
   * already redacted - it names the failure, never the value.
   */
  const probe = (stored: Required<StoredInstall>, refused: boolean): HealthProbe => {
    const checkedAt = now().toISOString();
    const result: HealthProbe = refused
      ? {
          result: 'failed',
          latencyMs: 412,
          message: 'The vendor refused the credentials (401 Unauthorized).',
          checkedAt,
          errorClass: 'CredentialRejectedError',
        }
      : { result: 'healthy', latencyMs: 184, message: null, checkedAt, errorClass: null };
    stored.health = result.result;
    stored.lastHealthAt = checkedAt;
    stored.lastError = result.message;
    stored.consecutiveFailures = refused ? stored.consecutiveFailures + 1 : 0;
    stored.checks.unshift({
      id: `00000000-0000-4000-b000-${String(stored.checks.length + 1).padStart(12, '0')}`,
      result: result.result === 'failed' ? 'failed' : 'healthy',
      latencyMs: result.latencyMs,
      errorClass: result.errorClass,
      message: result.message,
      checkedAt,
    });
    return result;
  };

  /** Whether the stored bundle is one the fake vendor refuses. */
  const refusedBundles = new WeakSet<StoredInstall>();

  const probeResult = (
    tenant: StoredTenant,
    entry: CatalogEntry,
    health: HealthProbe,
  ): InstallProbeResult => {
    const install = adminInstall(tenant, entry);
    if (install === null) throw new PlatformClientError('That integration is not enabled.');
    return { health, install };
  };

  /** Runs one mock handler, turning a synchronous throw into a rejection. */
  const attempt = <T>(work: () => T): Promise<T> => {
    try {
      return Promise.resolve(work());
    } catch (caught) {
      return Promise.reject(caught as Error);
    }
  };

  const countPlanTenants = (): void => {
    for (const plan of plans) {
      plan.tenantCount = tenants.filter((tenant) => tenant.planId === plan.id).length;
    }
  };
  countPlanTenants();

  return {
    /* ---------------------------------------------------------------- tenants */

    listTenants(query: TenantListQuery = {}) {
      const term = (query.q ?? '').trim().toLowerCase();
      const status = query.status ?? 'all';
      const planId = query.planId ?? 'all';
      const matching = tenants
        .filter((tenant) => status === 'all' || tenant.status === status)
        .filter((tenant) => planId === 'all' || tenant.planId === planId)
        .filter(
          (tenant) =>
            term === '' || tenant.name.toLowerCase().includes(term) || tenant.slug.includes(term),
        )
        .map(summarise);
      return Promise.resolve(paged(matching, query.cursor, query.limit ?? 50));
    },

    getTenant(tenantId) {
      return Promise.resolve(detail(find(tenantId)));
    },

    createTenant(input: CreateTenantRequest) {
      const problem = slugProblem(input.slug);
      if (problem !== null) return reject<never>(problem);
      if (tenants.some((tenant) => tenant.slug === input.slug)) {
        return reject<never>('That slug is already taken by another clinic.');
      }
      if (input.name.trim() === '') return reject<never>('A clinic name is required.');

      const planId =
        input.planId ??
        plans.find((plan) => plan.key === settings.defaultPlanKey)?.id ??
        plans.find((plan) => plan.isDefault)?.id ??
        plans[0]?.id ??
        '';

      const stamp = now().toISOString();
      const created: StoredTenant = {
        id: nextId('ten'),
        slug: input.slug,
        name: input.name.trim(),
        status: 'active',
        planId,
        timeZone: input.timeZone,
        statesServed: input.statesServed,
        retentionYears: settings.retentionYears,
        createdAt: stamp,
        suspendedReason: null,
        suspendedAt: null,
        archivedReason: null,
        archivedAt: null,
        branding: {
          ...BRANDING_EXTRAS,
          clinicName: input.name.trim(),
          logoFileId: null,
          primaryColor: settings.defaultBranding.primaryColor,
          secondaryColor: settings.defaultBranding.secondaryColor,
          accentColor: settings.defaultBranding.accentColor,
          supportEmail: null,
          supportPhone: null,
          privacyUrl: null,
          termsUrl: null,
        },
        domains: [
          platformSubdomain(nextId('dom'), input.slug, 'patient', stamp),
          platformSubdomain(nextId('dom'), input.slug, 'admin', stamp),
        ],
        overrides: {},
        installs: [],
        users: [],
        usage: { bookings: 0, cancellations: 0, notifications: 0 },
      };
      tenants.unshift(created);
      countPlanTenants();
      return Promise.resolve({
        tenant: summarise(created),
        /* Offline, an address asked for gets an invitation. The **API** answers `null`
           either way until M2.7's invite flow is reachable from the platform surface
           (API.md §2.13), which the screen reports rather than assuming. */
        invitation:
          input.adminEmail === undefined
            ? null
            : {
                id: nextId('inv'),
                email: input.adminEmail,
                expiresAt: new Date(now().getTime() + 7 * 24 * 3_600_000).toISOString(),
              },
      });
    },

    updateTenant(tenantId, patch: UpdateTenantRequest) {
      const tenant = find(tenantId);
      /* ADR-021: retention may be lengthened, never shortened. The API answers 409. */
      if (patch.retentionYears !== undefined && patch.retentionYears < tenant.retentionYears) {
        return reject<never>('Retention can be lengthened but never shortened (ADR-021).');
      }
      if (patch.planId !== undefined && !plans.some((plan) => plan.id === patch.planId)) {
        return reject<never>('That plan does not exist.');
      }
      if (patch.name !== undefined) tenant.name = patch.name.trim();
      if (patch.timeZone !== undefined) tenant.timeZone = patch.timeZone;
      if (patch.statesServed !== undefined) tenant.statesServed = [...patch.statesServed];
      if (patch.planId !== undefined) tenant.planId = patch.planId;
      if (patch.retentionYears !== undefined) tenant.retentionYears = patch.retentionYears;
      countPlanTenants();
      return Promise.resolve(summarise(tenant));
    },

    setTenantSuspended(tenantId, input) {
      try {
        requireReason(input.reason);
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      const tenant = find(tenantId);
      tenant.status = input.suspended ? 'suspended' : 'active';
      tenant.suspendedReason = input.suspended ? input.reason.trim() : null;
      tenant.suspendedAt = input.suspended ? now().toISOString() : null;
      return Promise.resolve(summarise(tenant));
    },

    archiveTenant(tenantId, input) {
      try {
        requireReason(input.reason);
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      const tenant = find(tenantId);
      tenant.status = 'archived';
      tenant.archivedReason = input.reason.trim();
      tenant.archivedAt = now().toISOString();
      return Promise.resolve(summarise(tenant));
    },

    /* ---------------------------------------------------------------- domains */

    listDomains(tenantId) {
      return Promise.resolve(find(tenantId).domains.map((domain) => ({ ...domain })));
    },

    verifyDomain(tenantId, domainId) {
      const tenant = find(tenantId);
      const domain = tenant.domains.find((candidate) => candidate.id === domainId);
      if (domain === undefined) return reject<never>('That domain does not exist.');
      // The mock always finds the records. The real check reads DNS and can answer
      // `failed` with `lastError` instead, which the screen already renders.
      const stamp = now().toISOString();
      domain.status = 'active';
      domain.verifiedAt = stamp;
      domain.activatedAt = stamp;
      domain.lastCheckAt = stamp;
      domain.lastError = null;
      return Promise.resolve({ ...domain });
    },

    /*
     * `POST /platform/tenants/:id/domains`, refusal for refusal (§3.1): the plan gate
     * first, then the platform domain and the edge itself, then the duplicate - which is
     * worded the way the server words it, **without naming the clinic that holds the
     * host**. Telling a support engineer which clinic already has `book.clinic.com` is
     * telling them something about another tenant they did not ask about.
     */
    addDomain(tenantId, input) {
      const host = input.host.trim().toLowerCase();
      if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(host)) {
        return reject<never>('Enter a hostname, such as book.clinic.com.');
      }
      const tenant = find(tenantId);
      if (!featuresOf(tenant).some((row) => row.key === 'custom_domains' && row.effective)) {
        return reject<never>('Custom domains are not enabled for this clinic.');
      }
      if (host === PLATFORM_DOMAIN || host.endsWith(`.${PLATFORM_DOMAIN}`)) {
        return reject<never>(
          'That address is part of the platform domain and is assigned automatically.',
        );
      }
      if (host === CNAME_TARGET) {
        return reject<never>('That address is the platform edge itself.');
      }
      if (tenants.some((each) => each.domains.some((domain) => domain.host === host))) {
        return reject<never>('That address is already in use.');
      }
      const created: TenantDomainDetail = {
        id: nextId('dom'),
        host,
        appKind: input.appKind,
        kind: 'custom',
        isPrimary: false,
        status: 'pending',
        cnameTarget: CNAME_TARGET,
        txtRecord: { name: `_ha-verify.${host}`, value: nextId('tok').replace(/\W/g, '') },
        verifiedAt: null,
        activatedAt: null,
        lastCheckAt: null,
        lastError: null,
      };
      tenant.domains.push(created);
      return Promise.resolve({ ...created });
    },

    removeDomain(tenantId, domainId) {
      const tenant = find(tenantId);
      const domain = tenant.domains.find((candidate) => candidate.id === domainId);
      if (domain === undefined) return reject<never>('We could not find that address.');
      if (domain.kind === 'platform_subdomain') {
        return reject<never>(
          'The clinic address we assign cannot be removed; it is how staff and patients reach you.',
        );
      }
      tenant.domains = tenant.domains.filter((candidate) => candidate.id !== domainId);
      return Promise.resolve();
    },

    /* --------------------------------------------------- branding and features */

    saveBranding(tenantId, input: SaveBrandingRequest) {
      /*
       * The API's refusals, per field, in the `VALIDATION_FAILED` envelope the real
       * client throws (`details.issues`), so the tab's per-field mapping is exercised
       * rather than assumed. The file-id rule is M17.1's ownership check: an id this
       * tenant did not upload (and of the right kind) is refused.
       */
      const tenant = find(tenantId);
      const issues: { path: string[]; code: string; message: string }[] = [];
      const hex = /^#[0-9a-fA-F]{6}$/;
      if (input.clinicName !== undefined && input.clinicName.trim() === '') {
        issues.push({
          path: ['clinicName'],
          code: 'too_small',
          message: 'A clinic name is required.',
        });
      }
      for (const key of [
        'primaryColor',
        'primaryHoverColor',
        'primarySubtleColor',
        'borderColor',
        'secondaryColor',
        'accentColor',
      ] as const) {
        const value = input[key];
        if (value !== undefined && value !== null && !hex.test(value)) {
          issues.push({
            path: [key],
            code: 'invalid_format',
            message: 'Use a six-digit hex colour, for example #18B3EF.',
          });
        }
      }
      if (
        input.supportEmail !== undefined &&
        input.supportEmail !== null &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.supportEmail)
      ) {
        issues.push({
          path: ['supportEmail'],
          code: 'invalid_format',
          message: 'Invalid email address',
        });
      }
      for (const [key, kind] of [
        ['logoFileId', 'logo'],
        ['faviconFileId', 'favicon'],
      ] as const) {
        const id = input[key];
        if (id === undefined || id === null || id === tenant.branding[key]) continue;
        if (uploads.get(id)?.tenantId !== tenantId || uploads.get(id)?.kind !== kind) {
          issues.push({
            path: [key],
            code: 'custom',
            message: `That ${kind} is not a file this clinic uploaded.`,
          });
        }
      }
      if (issues.length > 0) {
        return Promise.reject(
          new ApiError({
            status: 400,
            code: 'VALIDATION_FAILED',
            message: 'Some fields need a look.',
            correlationId: 'mock',
            details: { issues },
          }),
        );
      }

      const {
        logoUrl: _logoUrl,
        faviconUrl: _faviconUrl,
        contrast: _contrast,
        ...patch
      } = input as SaveBrandingRequest & Partial<PlatformTenantBranding>;
      const next: PlatformTenantBranding = {
        ...tenant.branding,
        ...patch,
        clinicName: input.clinicName?.trim() ?? tenant.branding.clinicName,
      };
      /* The read-only URLs follow the ids, as the API's signed ones do. */
      next.logoUrl = next.logoFileId === null ? null : fakeDownloadUrl(next.logoFileId);
      next.faviconUrl = next.faviconFileId === null ? null : fakeDownloadUrl(next.faviconFileId);
      next.contrast = contrastOf(next.primaryColor);
      tenant.branding = next;
      return Promise.resolve({ ...tenant.branding });
    },

    uploadTenantFile(tenantId, kind, file) {
      find(tenantId);
      if (!isContentTypeAllowedFor(kind, file.type)) {
        return reject<never>('That file type is not accepted here.');
      }
      if (file.size > FILE_KIND_RULES[kind].maxSizeBytes) {
        return reject<never>('That file is too large.');
      }
      /* A uuid, because `logoFileId` is `z.uuid()` on the wire. Deterministic per call. */
      uploadSequence += 1;
      const fileId = `00000000-0000-4000-8000-${String(uploadSequence).padStart(12, '0')}`;
      uploads.set(fileId, { tenantId, kind });
      /* No download route on the platform surface, so no URL: the form previews the
         picked file itself, exactly as it does against the API. */
      return Promise.resolve({ fileId, url: null });
    },

    listTenantFeatures(tenantId) {
      return Promise.resolve(featuresOf(find(tenantId)));
    },

    setTenantFeature(tenantId, featureKey, enabled) {
      const tenant = find(tenantId);
      /* The set is closed; an unknown key is a 404, not a silently written row. */
      if (!(FEATURE_KEYS as readonly string[]).includes(featureKey)) {
        return reject<never>('That feature does not exist.');
      }
      if (enabled === null) delete tenant.overrides[featureKey];
      else tenant.overrides[featureKey] = enabled;
      return Promise.resolve(featuresOf(tenant));
    },

    /* --------------------------------------------------------------- installs */

    listInstalls(tenantId) {
      return Promise.resolve(installsOf(find(tenantId)));
    },

    enableInstall(tenantId, integrationKey) {
      const tenant = find(tenantId);
      let entry: CatalogEntry;
      try {
        entry = catalogEntry(integrationKey);
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      if (entry.status === 'disabled') {
        return reject<never>('That integration is disabled in the catalog.');
      }
      const current = installsOf(tenant).find(
        (candidate) => candidate.integrationKey === integrationKey,
      );
      if (current !== undefined && !current.planAllows) {
        return reject<never>(
          "The tenant's plan does not include this integration. Change the plan or add a feature override first.",
        );
      }
      storedInstall(tenant, integrationKey).enabledByPlatform = true;
      return Promise.resolve(install(tenant, integrationKey));
    },

    disableInstall(tenantId, integrationKey, input) {
      try {
        requireReason(input.reason);
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      const tenant = find(tenantId);
      const stored = storedInstall(tenant, integrationKey);
      stored.enabledByPlatform = false;
      stored.isEnabled = false;
      return Promise.resolve(install(tenant, integrationKey));
    },

    getInstallDetail(tenantId, integrationKey) {
      return attempt(() => adminIntegration(find(tenantId), catalogEntry(integrationKey)));
    },

    putInstallCredentials(tenantId, integrationKey, body) {
      return attempt(() => {
        const tenant = find(tenantId);
        const { entry, stored } = enabledInstall(tenant, integrationKey);
        requireStepUp();

        const schema = entry.credentialSchema;
        const hadBundle = Object.keys(stored.credHint).length > 0;
        const issues: { path: string[]; message: string }[] = [];
        for (const name of schema.required ?? []) {
          const secret = schema.properties[name]?.secret === true;
          if (body.credentials[name] === undefined && !(secret && hadBundle)) {
            issues.push({ path: ['credentials', name], message: 'This one is needed.' });
          }
        }
        if (issues.length > 0) {
          throw new ApiError({
            status: 400,
            code: 'VALIDATION_FAILED',
            message: 'Some credentials are missing.',
            correlationId: 'mock',
            details: { issues },
          });
        }

        /* Only the mask survives: the values are dropped on this tick, as the API does. */
        let refused = false;
        for (const [name, value] of Object.entries(body.credentials)) {
          const text = String(value);
          if (text.startsWith('invalid')) refused = true;
          if (schema.properties[name]?.secret === true) {
            stored.credHint[name] = `••••${text.slice(-4)}`;
          }
        }
        if (refused) refusedBundles.add(stored);
        else refusedBundles.delete(stored);
        if (body.settings !== undefined) stored.settings = { ...stored.settings, ...body.settings };
        stored.credUpdatedAt = now().toISOString();
        stored.credUpdatedBy = 'Object Labs';
        return probeResult(tenant, entry, probe(stored, refused));
      });
    },

    deleteInstallCredentials(tenantId, integrationKey) {
      return attempt(() => {
        const tenant = find(tenantId);
        const { entry, stored } = enabledInstall(tenant, integrationKey);
        requireStepUp();
        stored.credHint = {};
        stored.credUpdatedAt = null;
        stored.credUpdatedBy = null;
        stored.health = 'unconfigured';
        stored.lastError = null;
        stored.isEnabled = false;
        refusedBundles.delete(stored);
        return adminIntegration(tenant, entry);
      });
    },

    patchInstallSettings(tenantId, integrationKey, patch) {
      return attempt(() => {
        const tenant = find(tenantId);
        const { entry, stored } = enabledInstall(tenant, integrationKey);
        stored.settings = { ...stored.settings, ...patch };
        return adminIntegration(tenant, entry);
      });
    },

    testInstall(tenantId, integrationKey) {
      return attempt(() => {
        const tenant = find(tenantId);
        const { entry, stored } = enabledInstall(tenant, integrationKey);
        if (Object.keys(stored.credHint).length === 0) {
          throw new ApiError({
            status: 409,
            code: 'CONFLICT',
            message: 'Add credentials before running a connection test.',
            correlationId: 'mock',
          });
        }
        return probeResult(tenant, entry, probe(stored, refusedBundles.has(stored)));
      });
    },

    listInstallCalls(tenantId, integrationKey, query = {}) {
      return attempt(() => {
        find(tenantId);
        const rows = calls
          .filter((call) => call.tenantId === tenantId && call.integrationKey === integrationKey)
          .filter((call) => query.status === undefined || call.status === query.status)
          .filter((call) => query.operation === undefined || call.operation === query.operation)
          .map(({ tenantId: _tenant, ...call }) => ({ ...call }));
        return paged(rows, query.cursor, query.limit ?? 20);
      });
    },

    listInstallHealth(tenantId, integrationKey, query = {}) {
      return attempt(() => {
        const { stored } = enabledInstall(find(tenantId), integrationKey);
        return paged([...stored.checks], query.cursor, query.limit ?? 10);
      });
    },

    /* ------------------------------------------------------------------ plans */

    listPlans() {
      return Promise.resolve(
        plans.map((plan) => ({
          ...plan,
          features: { ...plan.features },
          limits: { ...plan.limits },
        })),
      );
    },

    createPlan(input: CreatePlanRequest) {
      if (!/^[a-z0-9_-]{2,40}$/.test(input.key)) {
        return reject<never>('Use lower-case letters, numbers, _ and -.');
      }
      if (plans.some((plan) => plan.key === input.key)) {
        return reject<never>('That plan key is already taken.');
      }
      /* A row is written for every key: a missing row and `enabled:false` must not read
         differently (API.md §2.13). */
      const features = {} as Record<FeatureKey, boolean>;
      for (const key of FEATURE_KEYS) features[key] = false;
      for (const row of input.features ?? []) features[row.featureKey] = row.enabled;

      const created: Plan = {
        id: nextId('plan'),
        key: input.key,
        name: input.name.trim(),
        description: (input.description ?? '').trim(),
        monthlyPriceCents: input.monthlyPriceCents ?? 0,
        currency: 'USD',
        isDefault: false,
        status: 'active',
        tenantCount: 0,
        features,
        limits: {},
      };
      plans.push(created);
      return Promise.resolve({ ...created, features: { ...created.features } });
    },

    setPlanFeature(planId, featureKey, enabled) {
      const plan = plans.find((candidate) => candidate.id === planId);
      if (plan === undefined) return reject<never>('That plan does not exist.');
      plan.features[featureKey] = enabled;
      return Promise.resolve({ ...plan, features: { ...plan.features } });
    },

    /* ---------------------------------------------------------------- catalog */

    listCatalog() {
      return Promise.resolve(catalog.map((entry) => ({ ...entry })));
    },

    createCatalogEntry(input: CreateCatalogEntry) {
      if (!/^[a-z0-9][a-z0-9-]{1,40}$/.test(input.key)) {
        return reject<never>('Use lower-case letters, numbers and hyphens.');
      }
      if (catalog.some((entry) => entry.key === input.key)) {
        return reject<never>('That integration key already exists.');
      }
      const stamp = now().toISOString();
      const created: CatalogEntry = {
        id: nextId('int'),
        key: input.key,
        category: input.category,
        name: input.name.trim(),
        vendor: input.vendor ?? null,
        description: input.description ?? null,
        version: input.version,
        capabilities: input.capabilities ?? [],
        settingsSchema: input.settingsSchema,
        credentialSchema: input.credentialSchema,
        planFeatureKey: input.planFeatureKey ?? null,
        docsUrl: input.docsUrl ?? null,
        status: input.status ?? 'available',
        createdAt: stamp,
        updatedAt: stamp,
      };
      catalog.push(created);
      return Promise.resolve({ ...created });
    },

    updateCatalogEntry(key, patch: UpdateCatalogEntry) {
      let entry: CatalogEntry;
      try {
        entry = catalogEntry(key);
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      /* `key` and `category` are absent from the patch type on purpose: moving either
         would re-point live installs at code that does not implement their contract. */
      Object.assign(entry, patch, { updatedAt: now().toISOString() });
      return Promise.resolve({ ...entry });
    },

    /* ------------------------------------------------------------------ users */

    listUsers(query: PlatformUserListQuery = {}) {
      const term = (query.q ?? '').trim().toLowerCase();
      const matching = users
        .filter((user) => {
          if (term !== '' && !`${user.email} ${user.name}`.toLowerCase().includes(term)) {
            return false;
          }
          if (
            query.population !== undefined &&
            query.population !== 'all' &&
            user.population !== query.population
          ) {
            return false;
          }
          if (query.tenantId !== undefined && query.tenantId !== 'all') {
            if (!user.memberships.some((row) => row.tenantId === query.tenantId)) return false;
          }
          if (
            query.status !== undefined &&
            query.status !== 'all' &&
            user.status !== query.status
          ) {
            return false;
          }
          if (query.mfa === 'enrolled' && !user.mfaEnrolled) return false;
          if (query.mfa === 'missing' && user.mfaEnrolled) return false;
          return true;
        })
        .map(cloneUser);
      return Promise.resolve(paged(matching, query.cursor, query.limit ?? 50));
    },

    getUser(userId) {
      return Promise.resolve(userDetail(userId));
    },

    revokeUserSessions(userId) {
      try {
        findUser(userId);
        refuseSelf(userId, 'revoke sessions');
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      sessions[userId] = [];
      return Promise.resolve(userDetail(userId));
    },

    resetUserMfa(userId, input) {
      let user: PlatformUserSummary;
      try {
        user = findUser(userId);
        requireReason(input.reason);
        /* SECURITY.md §4.3: the two-person rule only means something if the second
           person is somebody else. */
        refuseSelf(userId, 'reset the second factor');
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      user.mfaEnrolled = false;
      sessions[userId] = [];
      return Promise.resolve(userDetail(userId));
    },

    setUserActive(userId, input) {
      let user: PlatformUserSummary;
      try {
        user = findUser(userId);
        requireReason(input.reason);
        refuseSelf(userId, 'change the status');
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      if (!input.active && user.platformRole === 'platform_admin' && admins().length <= 1) {
        return reject<never>('This is the last platform admin. Promote somebody else first.');
      }
      user.status = input.active ? 'active' : 'deactivated';
      if (!input.active) sessions[userId] = [];
      return Promise.resolve(cloneUser(user));
    },

    promoteUser(userId, input) {
      let user: PlatformUserSummary;
      try {
        user = findUser(userId);
        requireReason(input.reason);
        refuseSelf(userId, 'change the platform role');
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      if (input.confirmation.trim().toLowerCase() !== user.email.toLowerCase()) {
        return reject<never>("Type the account's own email address to confirm.");
      }
      if (user.population !== 'platform') {
        return reject<never>(
          'Only an Object Labs account can hold a platform role. Clinic staff are promoted inside their own tenant.',
        );
      }

      /* `platform_support` is granted at once; `platform_admin` opens a request a second
         administrator must confirm (SECURITY.md §6.2). */
      if (input.role === 'platform_support') {
        user.platformRole = 'platform_support';
        return Promise.resolve({ user: cloneUser(user), pending: null });
      }

      const requester = users.find((candidate) => candidate.id === self) ?? null;
      const pending: PendingPromotionRow = {
        id: nextId('prm'),
        targetUserId: user.id,
        role: 'platform_admin',
        requestedBy: self,
        requestedAt: now().toISOString(),
        expiresAt: new Date(now().getTime() + 24 * 3_600_000).toISOString(),
        status: 'pending',
        reason: input.reason,
        requestedByName: requester?.name ?? null,
        requestedByEmail: requester?.email ?? null,
        targetName: user.name,
        targetEmail: user.email,
      };
      promotions.push(pending);
      return Promise.resolve({ user: cloneUser(user), pending: { ...pending } });
    },

    /*
     * The queue the *second* administrator reads. `status` defaults to `pending`, and an
     * expired request is not pending whatever its column says - a to-do list with nothing
     * actionable on it is not one.
     */
    listPendingPromotions(query = {}) {
      const wanted = query.status ?? 'pending';
      const stamp = now().getTime();
      const rows = promotions
        .filter((row) => row.status === wanted)
        .filter((row) => wanted !== 'pending' || Date.parse(row.expiresAt) > stamp)
        .map((row) => ({ ...row }));
      return Promise.resolve(paged(rows, query.cursor, query.limit ?? 50));
    },

    confirmPromotion(userId, input) {
      let user: PlatformUserSummary;
      try {
        user = findUser(userId);
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      const pending = promotions.find(
        (row) => row.id === input.promotionId && row.status === 'pending',
      );
      if (pending === undefined) return reject<never>('That promotion is no longer waiting.');
      if (pending.requestedBy === self) {
        return reject<never>('A promotion must be confirmed by a second administrator.');
      }
      /* Confirmed, not deleted: the queue is also the record of what happened to a
         request, which is what `?status=confirmed` is for. */
      pending.status = 'confirmed';
      user.platformRole = pending.role;
      return Promise.resolve({ user: cloneUser(user), pending: null });
    },

    demoteUser(userId, input) {
      let user: PlatformUserSummary;
      try {
        user = findUser(userId);
        requireReason(input.reason);
        refuseSelf(userId, 'change the platform role');
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      if (input.confirmation.trim().toLowerCase() !== user.email.toLowerCase()) {
        return reject<never>("Type the account's own email address to confirm.");
      }
      if (user.platformRole === null) {
        return reject<never>('That account holds no platform role.');
      }
      if (user.platformRole === 'platform_admin' && admins().length <= 1) {
        return reject<never>('This is the last platform admin. Promote somebody else first.');
      }
      user.platformRole = null;
      sessions[userId] = [];
      return Promise.resolve({ user: cloneUser(user), pending: null });
    },

    invitePlatformUser(input: InvitePlatformUserRequest) {
      const email = input.email.trim().toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        return reject<never>('Enter a valid email address.');
      }
      if (users.some((user) => user.email.toLowerCase() === email)) {
        return reject<never>('An account with that address already exists.');
      }
      if (input.role === 'platform_admin') {
        return reject<never>('Invite as support, then promote: a promotion is its own decision.');
      }
      const created: PlatformUserSummary = {
        id: nextId('usr'),
        email,
        name: input.name.trim() === '' ? email : input.name.trim(),
        population: 'platform',
        platformRole: input.role,
        memberships: [],
        mfaEnrolled: false,
        lastSignInAt: null,
        status: 'invited',
      };
      users.unshift(created);
      return Promise.resolve(cloneUser(created));
    },

    /* ----------------------------------------------------------------- logs */

    listAudit(query: AuditQuery = {}) {
      const term = (query.action ?? '').trim().toLowerCase();
      const matching = audit
        .filter((row) => {
          if (query.tenantId !== undefined && query.tenantId !== 'all') {
            if (row.tenantId !== query.tenantId) return false;
          }
          if (
            query.actorId !== undefined &&
            query.actorId !== '' &&
            row.actorId !== query.actorId
          ) {
            return false;
          }
          if (term !== '' && !`${row.action} ${row.tableName}`.toLowerCase().includes(term)) {
            return false;
          }
          if (query.from !== undefined && query.from !== '' && row.occurredAt < query.from) {
            return false;
          }
          if (query.to !== undefined && query.to !== '' && row.occurredAt > query.to) return false;
          return true;
        })
        .map((row) => ({ ...row }));
      return Promise.resolve(paged(matching, query.cursor, query.limit ?? 25));
    },

    listIntegrationCalls(query: IntegrationCallFilters = {}) {
      const matching = calls
        .filter((call) => {
          if (query.tenantId !== undefined && query.tenantId !== 'all') {
            if (call.tenantId !== query.tenantId) return false;
          }
          if (query.integrationKey !== undefined && query.integrationKey !== 'all') {
            if (call.integrationKey !== query.integrationKey) return false;
          }
          if (
            query.status !== undefined &&
            query.status !== 'all' &&
            call.status !== query.status
          ) {
            return false;
          }
          if (query.from !== undefined && query.from !== '' && call.startedAt < query.from) {
            return false;
          }
          if (query.to !== undefined && query.to !== '' && call.startedAt >= query.to) return false;
          return true;
        })
        .map((call) => ({ ...call }));
      return Promise.resolve(paged(matching, query.cursor, query.limit ?? 25));
    },

    listQueues() {
      return Promise.resolve(
        queues.map((queue) => ({
          ...queue,
          deadLettered: deadLetters.filter((job) => job.sourceQueue === queue.name).length,
        })),
      );
    },

    listDeadLetters(query = {}) {
      const wanted = query.queue;
      return Promise.resolve(
        deadLetters
          .filter((job) => wanted === undefined || wanted === 'all' || job.sourceQueue === wanted)
          .slice(0, query.limit ?? 50)
          .map((job) => ({ ...job })),
      );
    },

    redriveJob(jobId, input) {
      try {
        requireReason(input.reason);
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      const index = deadLetters.findIndex((job) => job.id === jobId);
      if (index === -1) return reject<never>('That job is no longer dead-lettered.');
      const [job] = deadLetters.splice(index, 1);
      const queue = queues.find((candidate) => candidate.name === job?.sourceQueue);
      if (queue !== undefined) {
        queue.created += 1;
        queue.depth = queue.created + queue.retry;
      }
      return Promise.resolve(deadLetters.map((row) => ({ ...row })));
    },

    discardJob(jobId, input) {
      try {
        requireReason(input.reason);
      } catch (caught) {
        return Promise.reject(caught as Error);
      }
      const index = deadLetters.findIndex((job) => job.id === jobId);
      if (index === -1) return reject<never>('That job is no longer dead-lettered.');
      deadLetters.splice(index, 1);
      return Promise.resolve(deadLetters.map((row) => ({ ...row })));
    },

    getRequestSummary(range) {
      return Promise.resolve(requestSeed(range));
    },

    listErrorGroups(_range) {
      return Promise.resolve(errorSeed(settings.sentryUrl));
    },

    /* ------------------------------------------------------------- settings */

    getSettings() {
      return Promise.resolve(cloneSettings(settings));
    },

    saveSettings(patch: PlatformSettingsPatch) {
      const problems = settingsProblems(patch);
      const first = Object.values(problems)[0];
      if (first !== undefined) return reject<never>(first);

      if (patch.pricingTexts !== undefined) settings.pricingTexts = { ...patch.pricingTexts };
      if (patch.retentionYears !== undefined) settings.retentionYears = patch.retentionYears;
      if (patch.defaultPlanKey !== undefined) settings.defaultPlanKey = patch.defaultPlanKey;
      if (patch.defaultTimeZone !== undefined) settings.defaultTimeZone = patch.defaultTimeZone;
      if (patch.defaultBranding !== undefined) {
        settings.defaultBranding = { ...patch.defaultBranding };
      }
      if (patch.maintenanceMode !== undefined) {
        settings.maintenanceMode = { ...patch.maintenanceMode };
      }
      if (patch.sentryUrl !== undefined) settings.sentryUrl = patch.sentryUrl;
      if (patch.monitorUrl !== undefined) settings.monitorUrl = patch.monitorUrl;
      if (patch.contacts !== undefined)
        settings.contacts = patch.contacts.map((row) => ({ ...row }));
      return Promise.resolve(cloneSettings(settings));
    },

    /* ------------------------------------------------------------- feedback */

    listFeedback(query: FeedbackQuery = {}) {
      const matching = feedback
        .filter((item) => {
          if (query.tenantId !== undefined && query.tenantId !== 'all') {
            const wanted = query.tenantId === 'platform' ? null : query.tenantId;
            if (item.tenantId !== wanted) return false;
          }
          if (query.category !== undefined && query.category !== 'all') {
            if (item.category !== query.category) return false;
          }
          if (
            query.status !== undefined &&
            query.status !== 'all' &&
            item.status !== query.status
          ) {
            return false;
          }
          if (query.visibility !== undefined && query.visibility !== 'all') {
            if (item.visibility !== query.visibility) return false;
          }
          return true;
        })
        .map(cloneFeedback);
      return Promise.resolve(paged(matching, query.cursor, query.limit ?? 50));
    },

    updateFeedback(itemId, patch: ModerateFeedbackRequest) {
      const item = feedback.find((candidate) => candidate.id === itemId);
      if (item === undefined) return reject<never>('That feedback item does not exist.');

      if (patch.visibility !== undefined) {
        const refusal = visibilityProblem(item, patch.visibility);
        if (refusal !== null) return reject<never>(refusal);
        item.visibility = visibilityFor(item.category, patch.visibility);
      }

      if (patch.duplicateOfId !== undefined) {
        if (patch.duplicateOfId === itemId) {
          return reject<never>('An item cannot be a duplicate of itself.');
        }
        if (
          patch.duplicateOfId !== null &&
          !feedback.some((candidate) => candidate.id === patch.duplicateOfId)
        ) {
          return reject<never>('That target item does not exist.');
        }
        item.duplicateOfId = patch.duplicateOfId;
        item.status = patch.duplicateOfId === null ? 'open' : 'duplicate';
      }

      if (patch.status !== undefined) item.status = patch.status;
      if (patch.response !== undefined) {
        item.response = patch.response;
        item.respondedAt = patch.response === null ? null : now().toISOString();
      }
      item.updatedAt = now().toISOString();

      return Promise.resolve(cloneFeedback(item));
    },

    commentOnFeedback(itemId, input) {
      const item = feedback.find((candidate) => candidate.id === itemId);
      if (item === undefined) return reject<never>('That feedback item does not exist.');
      if (input.body.trim() === '') return reject<never>('Write something before posting.');
      item.comments.push({
        id: nextId('fbc'),
        authorName: 'Sam Rivera',
        body: input.body.trim(),
        isInternal: input.isInternal,
        createdAt: now().toISOString(),
      });
      return Promise.resolve(cloneFeedback(item));
    },

    /* --------------------------------------------------------------- health */

    getPlatformHealth(): Promise<PlatformHealth> {
      const counts = tenants.map(healthOf);
      return Promise.resolve({
        database: 'ok',
        queue: 'ok',
        queueDepth: queues.reduce((total, queue) => total + queue.depth, 0),
        queuesFailed: queues.reduce((total, queue) => total + queue.failed, 0),
        deadLetters: deadLetters.length,
        failedInstalls: counts.reduce((total, row) => total + row.failed, 0),
        degradedInstalls: counts.reduce((total, row) => total + row.degraded, 0),
        suspendedTenants: tenants.filter((tenant) => tenant.status === 'suspended').length,
        maintenanceMode: settings.maintenanceMode.enabled,
        checkedAt: now().toISOString(),
      });
    },

    listTenantHealth(): Promise<TenantHealthRow[]> {
      return Promise.resolve(
        tenants.map((tenant) => ({
          tenantId: tenant.id,
          tenantName: tenant.name,
          tenantSlug: tenant.slug,
          status: tenant.status,
          installs: tenant.installs
            .filter((row) => row.enabledByPlatform)
            .map((row) => ({ key: row.integrationKey, health: row.health })),
          failedJobs: deadLetters.filter((job) => job.tenantId === tenant.id).length,
        })),
      );
    },

    getUsageReport(query = {}) {
      const wanted = query.tenantId;
      const rows: TenantUsageRow[] = tenants
        .filter((tenant) => wanted === undefined || tenant.id === wanted)
        .map((tenant) => ({
          ...usageOf(tenant),
          tenantId: tenant.id,
          tenantName: tenant.name,
          tenantSlug: tenant.slug,
          planKey: planOf(tenant).key,
          status: tenant.status,
        }));
      return Promise.resolve({
        range: {
          from: query.from ?? new Date(Date.parse(SEED_NOW) - 30 * 24 * 3_600_000).toISOString(),
          to: query.to ?? SEED_NOW,
        },
        tenants: rows,
      });
    },
  };
}

/** The default mock instance, for `VITE_USE_MOCKS=true` and for tests that want a seed. */
export const platformClient: PlatformClient = createMockPlatformClient();
